import dayjs from 'dayjs';
import {useReservation} from 'context/reservation';
import {useFetchCheckoutSettings} from './useFetchCheckoutSettings';
import {useSummary} from 'context/summary';
import {useLayoutMode} from 'hooks/useLayoutMode';
import {OFFER_STATUSES, ORIGINS} from '@chekin/core';
import ChekinProSettings from 'ChekinProSettings';
import {IS_SDK_MODE} from 'routes/constants';

const AVAILABLE_ORIGINS = [ORIGINS.guesty2, ORIGINS.oracle, ORIGINS.mews2];

function getIsCheckoutDate(currentCheckoutDate?: string, checkoutTime?: string): boolean {
  if (!currentCheckoutDate || !checkoutTime) {
    return false;
  }
  const currentDate = dayjs();
  const checkoutDate = dayjs(currentCheckoutDate);
  const currentTime = currentDate.format('HH:mm');

  return checkoutDate.isSameOrBefore(currentDate, 'day') && currentTime >= checkoutTime;
}

function useCheckoutSettings() {
  const {housing, data: reservation} = useReservation();
  const {data: summary} = useSummary();
  const layoutMode = useLayoutMode();

  const isAvailableOrigins =
    reservation?.origin && AVAILABLE_ORIGINS.includes(reservation.origin);

  const isCheckoutEnabled =
    layoutMode === 'default' &&
    !IS_SDK_MODE &&
    !!housing?.check_out_settings_id &&
    isAvailableOrigins;

  const {data: checkoutSettings, isLoading} = useFetchCheckoutSettings({
    checkoutSettingsId: housing?.check_out_settings_id,
    enabled: Boolean(isCheckoutEnabled),
  });

  const hasMozio =
    summary?.mozio_status === OFFER_STATUSES.active && !ChekinProSettings.isSDK;
  const isCheckoutDate = getIsCheckoutDate(
    reservation?.check_out_date,
    checkoutSettings?.check_out_button_availability_time,
  );
  const isSurveyFlowEnabled = checkoutSettings?.is_survey_enabled;
  const isCheckoutCompleted = isSurveyFlowEnabled
    ? reservation?.check_out_flow_status === 'FEEDBACK_SUBMITTED'
    : reservation?.check_out_flow_status === 'PAID';

  const isAvailable = Boolean(housing?.check_out_settings_id) && isCheckoutDate;

  return {
    checkoutSettings,
    isAvailable,
    checkoutFlowStatus: reservation?.check_out_flow_status,
    isSurveyFlowEnabled,
    isCheckoutCompleted: !isLoading && isCheckoutCompleted,
    isLoading,
    isCheckoutEnabled,
    hasMozio,
  };
}

export {useCheckoutSettings};
